import { Children, Fragment, isValidElement, type ReactNode } from 'react';
import { Controller, FieldValues, Path, useFormContext } from 'react-hook-form';

import Box from '@mui/material/Box';
import Checkbox, { type CheckboxProps } from '@mui/material/Checkbox';
import Chip, { type ChipProps } from '@mui/material/Chip';
import FormControl, { type FormControlProps } from '@mui/material/FormControl';
import FormHelperText, { type FormHelperTextProps } from '@mui/material/FormHelperText';
import InputLabel, { type InputLabelProps as MuiInputLabelProps } from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select, { type SelectProps } from '@mui/material/Select';
import { type SxProps, type Theme } from '@mui/material/styles';
import TextField, { type TextFieldProps } from '@mui/material/TextField';

// ----------------------------------------------------------------------

interface RHFSelectProps<TFieldValues extends FieldValues = FieldValues>
  extends Omit<TextFieldProps, 'name' | 'select' | 'children' | 'slotProps' | 'onChange'> {
  name: Path<TFieldValues>;
  native?: boolean;
  children?: ReactNode;
  helperText?: string;
  /** DOM select values arrive as strings; intended for string-backed fields. */
  onValueChange?: (value: string) => void;
  slotProps?: {
    paper?: SxProps<Theme>;
    inputLabel?: MuiInputLabelProps;
  };
}

const flattenSelectChildren = (children: ReactNode): ReactNode[] =>
  Children.toArray(children).flatMap((child) =>
    isValidElement<{ children?: ReactNode }>(child) && child.type === Fragment
      ? flattenSelectChildren(child.props.children)
      : child
  );

export function RHFSelect<TFieldValues extends FieldValues = FieldValues>({
  name,
  native,
  children,
  slotProps,
  helperText,
  onValueChange,
  ...other
}: RHFSelectProps<TFieldValues>) {
  const { control } = useFormContext<TFieldValues>();

  const labelId = `${name}-select-label`;

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState: { error } }) => (
        <TextField
          {...field}
          fullWidth
          select
          error={!!error}
          helperText={error ? error?.message : helperText}
          slotProps={{
            inputLabel: { htmlFor: labelId, ...slotProps?.inputLabel },
            htmlInput: { id: labelId },
            select: {
              native,
              MenuProps: { PaperProps: { sx: { maxHeight: 220, ...slotProps?.paper } } },
              sx: { textTransform: 'capitalize' },
            },
            input: { id: labelId },
          }}
          onChange={(event) => {
            field.onChange(event);
            onValueChange?.(event.target.value);
          }}
          {...other}
        >
          {flattenSelectChildren(children)}
        </TextField>
      )}
    />
  );
}

// ----------------------------------------------------------------------

type MultiSelectOption = { value: string | number; label: string };

interface RHFMultiSelectProps<TFieldValues extends FieldValues = FieldValues>
  extends Omit<FormControlProps, 'name'> {
  name: Path<TFieldValues>;
  chip?: boolean;
  label?: React.ReactNode;
  options: MultiSelectOption[];
  checkbox?: boolean;
  placeholder?: string;
  slotProps?: {
    select?: SelectProps;
    chip?: ChipProps;
    checkbox?: CheckboxProps;
    inputLabel?: MuiInputLabelProps;
    formHelperText?: FormHelperTextProps;
  };
  helperText?: string;
}

export function RHFMultiSelect<TFieldValues extends FieldValues = FieldValues>({
  name,
  chip,
  label,
  options,
  checkbox,
  placeholder,
  slotProps,
  helperText,
  ...other
}: RHFMultiSelectProps<TFieldValues>) {
  const { control } = useFormContext<TFieldValues>();

  const labelId = `${name}-select-label`;
  const selectId = `${name}-select`;

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState: { error } }) => (
        <FormControl error={!!error} {...other}>
          {label && (
            <InputLabel htmlFor={selectId} id={labelId} {...slotProps?.inputLabel}>
              {label}
            </InputLabel>
          )}

          <Select
            {...field}
            multiple
            displayEmpty={!!placeholder}
            label={label}
            labelId={label ? labelId : undefined}
            renderValue={(selected) => {
              const selectedArray = Array.isArray(selected)
                ? (selected as (string | number)[])
                : [];
              const selectedItems = options.filter((item) => selectedArray.includes(item.value));

              if (!selectedItems.length && placeholder) {
                return <Box sx={{ color: 'text.disabled' }}>{placeholder}</Box>;
              }

              if (chip) {
                return (
                  <Box sx={{ gap: 0.5, display: 'flex', flexWrap: 'wrap' }}>
                    {selectedItems.map((item) => (
                      <Chip
                        key={String(item.value)}
                        label={item.label}
                        size="small"
                        variant="outlined"
                        {...slotProps?.chip}
                      />
                    ))}
                  </Box>
                );
              }

              return selectedItems.map((item) => item.label).join(', ');
            }}
            {...slotProps?.select}
            inputProps={{ id: selectId, ...slotProps?.select?.inputProps }}
          >
            {options.map((option) => (
              <MenuItem key={String(option.value)} value={option.value}>
                {checkbox && (
                  <Checkbox
                    disableRipple
                    checked={
                      Array.isArray(field.value) ? field.value.includes(option.value) : false
                    }
                    size="small"
                    {...slotProps?.checkbox}
                  />
                )}

                {option.label}
              </MenuItem>
            ))}
          </Select>

          {(!!error || helperText) && (
            <FormHelperText error={!!error} {...slotProps?.formHelperText}>
              {error ? error?.message : helperText}
            </FormHelperText>
          )}
        </FormControl>
      )}
    />
  );
}
